import React, { useEffect, useState, useRef, lazy } from 'react';
import confetti from 'canvas-confetti';
import { PhoneIcon } from 'lucide-react';
function CtaBanner() {
  const [showNumber, setShowNumber] = useState(false);
  return <section className="py-16 md:py-24 px-6 bg-white" data-id="element-71">
      <div className="max-w-5xl mx-auto" data-id="element-72">
        <div className="rounded-[2rem] px-10 py-14 md:px-20 md:py-20 border border-yellow-300" style={{
        backgroundColor: '#FFFC00'
      }} data-id="element-73">
          <div className="text-center mb-10" data-id="element-74">
            <p className="md:text-lg font-bold uppercase tracking-wider mb-4 drop-shadow-sm text-[24px] text-[#F4650B] bg-[#F2FF00]" data-id="element-75">
              Limited to 10 cafés
            </p>
            <h3 className="text-4xl md:text-6xl font-black text-neutral-900 mb-6 tracking-tight" data-id="element-76">
              Become a Founding Café
            </h3>
            <p className="text-2xl md:text-3xl text-neutral-800 max-w-2xl mx-auto leading-relaxed font-medium" data-id="element-77">
              Turn first-timers into regulars with Kraya's social features and
              automatic loyalty rewards — no hardware needed.
            </p>
          </div>

          <div className="flex flex-col items-center gap-6" data-id="element-78">
            <p className="text-xl md:text-2xl font-bold drop-shadow-sm text-[#EF1E13]" data-id="element-79">
              If we caught your attention already, speak to us directly
            </p>
            <button onClick={() => setShowNumber(true)} className="inline-flex items-center gap-3 px-10 py-5 bg-black text-white font-bold rounded-full hover:bg-neutral-900 transition-colors shadow-2xl text-lg md:text-xl" data-id="element-80">
              <PhoneIcon className="w-6 h-6" data-id="element-81" />
              {showNumber ? <a href="tel:+917989586935" className="text-white no-underline" data-id="element-82">
                  +91-7989586935
                </a> : 'Call Us'}
            </button>
          </div>
        </div>
      </div>
    </section>;
}
const cafes = [{
  name: 'Roast Coffee House',
  stat: '1.2K regular visitors',
  image: 'https://images.unsplash.com/photo-1497935586351-b67a49e012bf?w=400&h=300&fit=crop'
}, {
  name: 'Blue Cup Café',
  stat: '1.5K visits this month',
  image: 'https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=400&h=300&fit=crop'
}, {
  name: 'Your Coffee Shop',
  stat: '150 repeat customers',
  image: 'https://images.unsplash.com/photo-1509042239860-f550ce710b93?w=400&h=300&fit=crop'
}, {
  name: 'Bean Street',
  stat: '1.1K loyal customers',
  image: 'https://images.unsplash.com/photo-1442512595331-e89e73853f31?w=400&h=300&fit=crop'
}];
const PICKED_CAFE = 2;
type AnimPhase = 'scroll' | 'pause' | 'zoom' | 'celebrate' | 'reset';
function CafeCarousel({
  cafes
}: {
  cafes: {
    name: string;
    stat: string;
    image: string;
  }[];
}) {
  const [phase, setPhase] = useState<AnimPhase>('scroll');
  const cancelled = useRef(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const pickedCardRef = useRef<HTMLDivElement>(null);
  const wait = (ms: number) => new Promise<void>(res => {
    const t = setTimeout(res, ms);
    timers.current.push(t);
  });
  useEffect(() => {
    cancelled.current = false;
    const run = async () => {
      while (!cancelled.current) {
        setPhase('scroll');
        await wait(5000);
        if (cancelled.current) return;
        setPhase('pause');
        await wait(500);
        if (cancelled.current) return;
        setPhase('zoom');
        await wait(700);
        if (cancelled.current) return;
        setPhase('celebrate');
        // Fire canvas-confetti from the picked card's position
        if (pickedCardRef.current) {
          const rect = pickedCardRef.current.getBoundingClientRect();
          const x = (rect.left + rect.width / 2) / window.innerWidth;
          const y = (rect.top + rect.height / 3) / window.innerHeight;
          confetti({
            particleCount: 80,
            spread: 70,
            origin: {
              x,
              y
            },
            colors: ['#f59e0b', '#fbbf24', '#f97316', '#ef4444', '#8b5cf6', '#3b82f6', '#10b981', '#ec4899'],
            ticks: 120,
            gravity: 1.2,
            scalar: 1.1
          });
        }
        await wait(3000);
        if (cancelled.current) return;
        setPhase('reset');
        await wait(800);
      }
    };
    run();
    return () => {
      cancelled.current = true;
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
  }, []);
  const cards = [...cafes, ...cafes, ...cafes];
  const isPickPhase = phase === 'zoom' || phase === 'celebrate';
  const labelText = isPickPhase ? 'Customer chooses your café! ✨' : 'Browsing cafés...';
  return <div className="hidden md:block relative w-full" style={{
    minHeight: '520px'
  }} data-id="element-83">
      {/* Animated label capsule — left-aligned, protruding from edge */}
      <div className="flex justify-start pl-6 md:pl-12 mb-6" data-id="element-84">
        <span className={`inline-block px-8 py-3 rounded-full text-base md:text-lg font-bold transition-all duration-500 ${isPickPhase ? 'bg-amber-500 text-white shadow-xl scale-105' : 'bg-neutral-200 text-neutral-600'}`} data-id="element-85">
          {labelText}
        </span>
      </div>

      <div className="w-full" style={{
      overflow: 'visible',
      paddingTop: '6rem',
      paddingBottom: '6rem'
    }} data-id="element-86">
        <div className={`flex items-end gap-6 w-max ${phase === 'scroll' ? 'animate-carousel-roll' : ''}`} style={{
        animationPlayState: phase === 'scroll' ? 'running' : 'paused'
      }} data-id="element-87">
          {cards.map((cafe, idx) => {
          const isPicked = idx === PICKED_CAFE;
          const isZoomed = isPicked && isPickPhase;
          const isCelebrating = isPicked && phase === 'celebrate';
          const isDimmed = !isPicked && isPickPhase;
          return <div key={`${cafe.name}-${idx}`} ref={isPicked ? pickedCardRef : undefined} className="relative flex-shrink-0 w-[260px]" style={{
            zIndex: isZoomed ? 50 : 1
          }} data-id="element-88">
                {/* Card */}
                <div style={{
              transform: isZoomed ? 'scale(1.45)' : isDimmed ? 'scale(0.9)' : 'scale(1)',
              opacity: isDimmed ? 0.3 : 1,
              transformOrigin: 'bottom center',
              transition: 'transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.4s ease, box-shadow 0.4s ease, border-color 0.4s ease',
              borderWidth: isZoomed ? '2px' : '1px',
              borderColor: isZoomed ? '#f59e0b' : 'rgba(229,229,229,0.7)',
              borderStyle: 'solid',
              boxShadow: isZoomed ? '0 25px 50px -12px rgba(0,0,0,0.2)' : '0 1px 3px rgba(0,0,0,0.06)'
            }} className={`bg-white rounded-2xl overflow-hidden ${isCelebrating ? 'animate-card-wiggle' : ''}`} data-id="element-89">
                  <div className="aspect-[4/3] w-full" data-id="element-90">
                    <img src={cafe.image} alt={cafe.name} className="w-full h-full object-cover" loading="lazy" data-id="element-91" />
                  </div>
                  <div className="p-5" data-id="element-92">
                    <h3 className="text-lg font-semibold text-neutral-900 mb-1" data-id="element-93">
                      {cafe.name}
                    </h3>
                    <p className="text-sm text-neutral-600" data-id="element-94">{cafe.stat}</p>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </div>;
}
export const SnackpassStyle = () => {
  return <div className="bg-white w-full" data-id="element-95">
      {/* Section 1 - Hero */}
      <section className="py-16 md:py-24 px-6 bg-white" data-id="element-96">
        <div className="max-w-5xl mx-auto text-center" data-id="element-97">
          <h1 className="text-3xl md:text-5xl font-bold text-neutral-900 mb-6 leading-[1.08] tracking-tight" data-id="element-98">
            Your next customer?
            <br data-id="element-99" />
            They're on Kraya.
          </h1>
          <p className="text-2xl md:text-3xl text-neutral-600 max-w-2xl mx-auto mb-14" data-id="element-100">
            Tap into an exclusive network of people exploring cafés around the
            city.
          </p>
          <div className="aspect-[16/9] w-full max-h-[480px] overflow-hidden rounded-3xl shadow-xl" data-id="element-101">
            <img src="https://cdn.magicpatterns.com/uploads/gQB76qs3wDBAUE9NbxS393/final.png" alt="Friends sharing coffee at Cafe Bean's on Kraya" className="w-full h-full object-cover" loading="lazy" data-id="element-102" />
          </div>
        </div>
      </section>

      {/* Section 2 - image left, text right */}
      <section className="py-14 md:py-20 px-6 bg-neutral-50" data-id="element-103">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 md:gap-20 items-center" data-id="element-104">
          <div className="aspect-[4/3] w-full overflow-hidden rounded-3xl order-1 shadow-lg" data-id="element-105">
            <img src="https://cdn.magicpatterns.com/uploads/4WMH8PwBuFw1cyJhh8iKd9/Gemini_Generated_Image_g39a4qg39a4qg39a.png" alt="Arka Coffee app experience mockup" className="w-full h-full object-cover" loading="lazy" data-id="element-106" />
          </div>
          <div className="order-2" data-id="element-107">
            <h2 className="text-3xl md:text-5xl font-bold text-neutral-900 mb-5 leading-tight tracking-tight" data-id="element-108">
              Put your café in front of the right crowd
            </h2>
            <p className="text-xl md:text-2xl text-neutral-600 leading-relaxed" data-id="element-109">
              Real visits, real rewards, and repeat customers — all in one
              platform.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <CtaBanner data-id="element-110" />

      {/* Section 3 - Text top, photo + phone below */}
      <section className="py-14 md:py-20 px-6 bg-white" data-id="element-111">
        <div className="max-w-6xl mx-auto" data-id="element-112">
          {/* Top: Left-aligned heading + subtitle, tight to photo */}
          <div className="mb-0 relative z-10" data-id="element-113">
            <h2 className="text-3xl md:text-5xl font-bold text-neutral-900 mb-2 leading-tight tracking-tight" data-id="element-114">
              Grow faster with social visits
            </h2>
            <p className="text-xl md:text-2xl text-neutral-600 leading-relaxed max-w-md mb-0" data-id="element-115">
              Cafés on Kraya grow as people share, gift, and bring friends to
              their favorite spots.
            </p>
          </div>

          {/* Bottom: Photo left, phone mockup right */}
          <div className="grid md:grid-cols-2 gap-4 md:gap-6 items-start mt-6" data-id="element-116">
            <div className="w-full overflow-hidden rounded-3xl shadow-lg" data-id="element-117">
              <img src="https://cdn.magicpatterns.com/uploads/pCqUdcfpWZMgp5LpjfytCY/Gemini_Generated_Image_vuv6fkvuv6fkvuv6.png" alt="Friends enjoying coffee and games at a café" className="w-full h-auto object-contain" loading="lazy" data-id="element-118" />
            </div>

            {/* Floating phone mockup - right side, centered in column */}
            <div className="flex justify-center md:-mt-8" data-id="element-119">
              <div className="relative" style={{
              filter: 'drop-shadow(0 25px 50px rgba(0,0,0,0.15))',
              WebkitMaskImage: 'radial-gradient(ellipse 85% 85% at center, black 55%, transparent 100%)',
              maskImage: 'radial-gradient(ellipse 85% 85% at center, black 55%, transparent 100%)'
            }} data-id="element-120">
                <img src="https://cdn.magicpatterns.com/uploads/sLgDBoay5QTfFJkEJsRCB6/Gemini_Generated_Image_xxi3n3xxi3n3xxi3.png" alt="Kraya app showing social café meetup feature" className="w-64 md:w-80 h-auto object-contain" loading="lazy" data-id="element-121" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4 - Community proof */}
      <section className="py-14 md:py-20 px-6 bg-neutral-50 overflow-hidden" data-id="element-122">
        <div className="max-w-6xl mx-auto" data-id="element-123">
          <div className="text-center mb-16 md:mb-20" data-id="element-124">
            <h2 className="text-3xl md:text-5xl font-bold text-neutral-900 leading-tight tracking-tight max-w-3xl mx-auto" data-id="element-125">
              Become a favorite in your neighborhood
            </h2>
          </div>

          {/* Mobile: static 2x2 grid */}
          <div className="grid grid-cols-2 gap-6 md:hidden" data-id="element-126">
            {cafes.map(cafe => <div key={cafe.name} className="relative bg-white rounded-2xl border border-neutral-200/70 shadow-sm" data-id="element-127">
                <div className="aspect-[4/3] w-full overflow-hidden rounded-t-2xl" data-id="element-128">
                  <img src={cafe.image} alt={cafe.name} className="w-full h-full object-cover" loading="lazy" data-id="element-129" />
                </div>
                <div className="p-4" data-id="element-130">
                  <h3 className="text-sm font-semibold text-neutral-900 mb-1" data-id="element-131">
                    {cafe.name}
                  </h3>
                  <p className="text-xs text-neutral-600" data-id="element-132">{cafe.stat}</p>
                </div>
              </div>)}
          </div>

          {/* Desktop: animated marquee with pick animation */}
          <CafeCarousel cafes={cafes} data-id="element-133" />
        </div>
      </section>
      {/* Section 5 - Closing */}
      <section className="py-14 md:py-20 px-6 bg-white" data-id="element-134">
        <div className="max-w-5xl mx-auto text-center" data-id="element-135">
          <h2 className="text-3xl md:text-5xl font-bold text-neutral-900 mb-6 leading-tight tracking-tight" data-id="element-136">
            Where cafés connect with real customers
          </h2>
          <p className="text-xl md:text-2xl text-neutral-600 max-w-md mx-auto mb-14" data-id="element-137">
            Kraya helps you reach, engage, and grow
            <br data-id="element-138" />a loyal customer base — without relying on ads.
          </p>

          <div className="aspect-[16/7] w-full max-w-3xl mx-auto overflow-hidden rounded-3xl shadow-xl" data-id="element-139">
            <img src="https://images.unsplash.com/photo-1525610553991-2bede1a236e2?w=1200&h=600&fit=crop" alt="Cozy neighborhood café storefront" className="w-full h-full object-cover" loading="lazy" data-id="element-140" />
          </div>
        </div>
      </section>
    </div>;
};